import axios from "axios";
import { buildP2PStats } from "./stats";
import { ALLOWED_FIATS, DEFAULT_P2P_LIMIT } from "./types";
import { formatRequestError } from "./errors";

const GATE_ENDPOINT = "/api/gate-p2p/json_svr/query_fiat_advert";

const normalizeSide = (side) => {
  const candidate = String(side || "").toLowerCase();
  if (candidate === "sell" || candidate === "buy") return candidate;
  throw new Error("Side inválido para Gate.io P2P");
};

/**
 * Consulta preços P2P da Gate.io.
 * @param {Object} options
 * @param {"BRL"|"BOB"} options.fiat
 * @param {"buy"|"sell"} options.side
 * @param {number} [options.limit]
 * @returns {Promise<import("./types").P2PQuote>}
 */
export async function fetchGateP2P({ fiat, side, limit = DEFAULT_P2P_LIMIT }) {
  const normalizedFiat = String(fiat || "").toUpperCase();
  if (!ALLOWED_FIATS.includes(normalizedFiat)) {
    throw new Error("Fiat inválido para Gate.io P2P");
  }
  const normalizedSide = normalizeSide(side);
  const payload = {
    fiat: normalizedFiat,
    crypto: "USDT",
    trade_type: normalizedSide,
    page: 1,
    limit,
  };
  // NOTE: Vite proxy resolve apenas ambiente dev; mova para backend em produção.
  const ensureJsonResponse = (exchange, fiat, candidate) => {
    const hasJson =
      candidate &&
      typeof candidate.data === "object" &&
      candidate.data !== null;
    if (hasJson) return;
    if (import.meta.env.MODE === "development") {
      const preview = typeof candidate?.data === "string"
        ? candidate.data.slice(0, 200)
        : "";
      console.warn(
        `[P2P][${exchange}][${fiat}] resposta não-JSON/WAF`,
        { status: candidate?.status, preview }
      );
    }
    const error = new Error("Resposta não-JSON/WAF da Gate.io");
    error.response = candidate;
    throw error;
  };
  let response;
  try {
    response = await axios.post(GATE_ENDPOINT, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    ensureJsonResponse("gate", normalizedFiat, response);
  } catch (error) {
    throw new Error(
      formatRequestError({
        exchange: "gate",
        fiat: normalizedFiat,
        side: normalizedSide.toUpperCase(),
        fallback: "Não foi possível obter anúncios válidos da Gate.io",
        error,
      })
    );
  }
  ensureJson("gate", normalizedFiat, response);
  const rawData = response.data?.data;
  const classifieds = Array.isArray(rawData) ? rawData : [];
  const ads = [];
  classifieds.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    if (Array.isArray(entry.ad)) {
      entry.ad.forEach((item) => ads.push(item));
      return;
    }
    if (Array.isArray(entry.ads)) {
      entry.ads.forEach((item) => ads.push(item));
      return;
    }
    if (entry.ad) {
      ads.push(entry.ad);
      return;
    }
    if (entry.price) {
      ads.push(entry);
      return;
    }
  });
  if (import.meta.env.MODE === "development" && !ads.length) {
    console.debug("Formato Gate P2P inesperado:", rawData);
  }
  const prices = ads
    .map((ad) => Number(ad?.price))
    .filter((price) => Number.isFinite(price));
  if (!prices.length) {
    throw new Error("Não foi possível obter anúncios válidos da Gate.io");
  }
  return buildP2PStats({
    exchange: "gate",
    fiat: normalizedFiat,
    side: normalizedSide.toUpperCase(),
    prices,
    raw: import.meta.env.MODE === "development" ? rawData : undefined,
  });
}
